import type { PSAAdapter, RawRow } from "@/lib/adapters/base";
import { applyFilters } from "@/lib/processing/filter";
import { normalizeRow } from "@/lib/processing/normalize";
import { runQualityChecks } from "@/lib/quality/detector";
import type { CatalogKitConfig, ProcessingResult } from "@/types";

/**
 * Run the full client-side pipeline over raw CSV rows: map → audit quality →
 * filter → normalize. Quality runs on the full mapped set *before* filtering so
 * issues like zero-price rows are not hidden, mirroring the Python `Processor`.
 *
 * Pure and synchronous — all I/O (parsing, downloads) lives outside.
 *
 * @param rawRows Parsed CSV rows (header → value).
 * @param adapter Adapter matching the input format.
 * @param config Resolved configuration.
 * @param now Reference time for the stale-pricing rule (defaults to the present).
 */
export function runPipeline(
  rawRows: RawRow[],
  adapter: PSAAdapter,
  config: CatalogKitConfig,
  now: Date = new Date(),
): ProcessingResult {
  const mapped = rawRows.map((row) => adapter.mapRow(row));
  const issues = runQualityChecks(mapped, config, now);
  const kept = applyFilters(mapped, config, adapter).map((row) => normalizeRow(row));
  return {
    totalRows: mapped.length,
    keptRows: kept.length,
    droppedRows: mapped.length - kept.length,
    rows: kept,
    issues,
  };
}
